import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useState } from 'react';
import Tag from '../../../../components/Display/Tag';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import { CONSTANT } from '../../../../utils/constants';
import ImageForm from '../../common/image/ImageForm';

function ProjectPanel({ isOpen, onClose, project, loading, onUpdate, onCreate }) {
  const [title, setTitle] = useState('');
  const [placeName, setPlaceName] = useState('');
  const [buildMaster, setBuildMaster] = useState('');
  const [isHighlight, setIsHighlight] = useState(true);
  const [year, setYear] = useState(2026);
  const [mainImage, setMainImage] = useState(undefined);

  const [description, setDescription] = useState('');
  const [surface, setSurface] = useState(0);
  const [cost, setCost] = useState(0);
  const [certifications, setCertifications] = useState('');

  const [category, setCategory] = useState('');
  const [conceptionTeam, setConceptionTeam] = useState('');
  const [constructionTeam, setConstructionTeam] = useState('');

  const [status, setStatus] = useState('draft'); // draft, published

  useEffect(() => {
    if (project) {
      setTitle(project.title);
      setYear(project.year);
      setIsHighlight(project.is_highlight);
      setDescription(project.description);
      setSurface(project.surface);
      setCost(project.cost);
      setCategory(project.category);
      setConceptionTeam(project.conception_team);
      setConstructionTeam(project.construction_team);
      setCertifications(project.certifications);
      setMainImage(project.main_image);
      setPlaceName(project.place_name);
      setBuildMaster(project.build_master);
      setStatus(project.status);
    } else {
      setTitle('');
      setYear(2026);
      setIsHighlight(false);
      setDescription('');
      setMainImage(undefined);
      setSurface(0);
      setCost(0);
      setPlaceName('');
      setCategory('');
      setConceptionTeam('');
      setConstructionTeam('');
      setCertifications('');
      setBuildMaster('');
      setStatus('draft');
    }
  }, [project, isOpen]);

  const handleValidate = () => {
    const projectPayload = {
      title,
      year,
      is_highlight: isHighlight,
      description,
      place_name: placeName,
      build_master: buildMaster,
      surface: surface,
      cost: cost,
      conception_team: conceptionTeam,
      construction_team: constructionTeam,
      category: category,
      certifications: certifications,
      main_image: mainImage?.id,
      status
    };
    project ? onUpdate(projectPayload) : onCreate(projectPayload);
  };

  const formIsValid = () => {
    return title && mainImage && year && placeName && buildMaster;
  };

  const dropdownTemplate = (option, props) => {
    if (option) {
      return (
        <Tag
          tag={{
            label: option.label,
            icon: option.icon,
            bgColor: option.bgColor
          }}
          rounded
        />
      );
    }
    return <span>{props.placeholder}</span>;
  };

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider la ${project ? 'modification' : 'création'}`}
        className="w-full"
        onClick={handleValidate}
        severity="success"
        disabled={!formIsValid()}
        loading={loading}
      />
    </div>
  );

  return (
    <Sidebar
      style={{ width: 700 }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${project ? 'Modifier' : 'Ajouter'} un projet`}</h3>
      }>
      <div className="sidebar-content-container">
        <div className="form-wrapper !pb-60">
          <FormInput label={'Titre du projet'} required isValid={title.length}>
            <InputText
              id="title"
              value={title}
              maxLength={300}
              placeholder="Veuillez saisir le titre du projet"
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormInput>

          <FormInput
            label={`${isHighlight ? 'Ce projet est mis en avant' : "Ce projet n'est pas mis en avant"}`}>
            <InputSwitch checked={isHighlight} onChange={(e) => setIsHighlight(e.value)} />
          </FormInput>

          <FormInput key="status" label={'État de la ressource'}>
            <Dropdown
              id="status"
              value={status}
              onChange={(e) => setStatus(e.value)}
              options={CONSTANT.prna.statusTypes}
              optionLabel="label"
              placeholder="Veuillez sélectionner l'état"
              valueTemplate={dropdownTemplate}
              itemTemplate={dropdownTemplate}
            />
          </FormInput>

          <FormInput label={'Localisation'} required isValid={placeName}>
            <InputText
              id="place_name"
              value={placeName}
              maxLength={300}
              placeholder="Veuillez saisir le nom de la localisation"
              onChange={(e) => setPlaceName(e.target.value)}
            />
          </FormInput>

          <FormInput label={"Maître d'ouvrage"} required isValid={buildMaster}>
            <InputText
              id="build_master"
              value={buildMaster}
              maxLength={300}
              placeholder="Veuillez saisir le nom du maître d'ouvrage"
              onChange={(e) => setBuildMaster(e.target.value)}
            />
          </FormInput>

          <FormInput label={'Année'} required isValid={year}>
            <InputNumber
              id="year"
              value={year}
              placeholder="Veuillez saisir l'année"
              onChange={(e) => setYear(e.value)}
              locale="fr"
            />
          </FormInput>

          <ImageForm
            image={mainImage}
            setImage={setMainImage}
            deleteImage={() => setMainImage(undefined)}
          />

          <div className={`flex flex-col gap-5 `}>
            <FormInput label={'Description'}>
              <RichTextEditor
                value={description}
                onChange={(value) => {
                  setDescription(value);
                }}
              />
            </FormInput>

            <FormInput label={'Catégorie'}>
              <InputText
                id="category"
                value={category}
                maxLength={400}
                placeholder="Veuillez saisir la catégorie"
                onChange={(e) => setCategory(e.target.value)}
              />
            </FormInput>

            <FormInput label={'Surface en m²'}>
              <InputNumber
                id="surface"
                value={surface}
                placeholder="Veuillez saisir la surface"
                onChange={(e) => setSurface(e.value)}
                locale="fr"
              />
            </FormInput>

            <FormInput label={'Budget en €'}>
              <InputNumber
                id="cost"
                value={cost}
                placeholder="Veuillez saisir le budget"
                onChange={(e) => setCost(e.value)}
                locale="fr"
              />
            </FormInput>

            <FormInput label={'Équipe de conception'}>
              <InputText
                id="conceptionTeam"
                value={conceptionTeam}
                maxLength={400}
                placeholder="Veuillez saisir le nom de l'équipe"
                onChange={(e) => setConceptionTeam(e.target.value)}
              />
            </FormInput>

            <FormInput label={'Équipe de construction'}>
              <InputText
                id="constructionTeam"
                value={constructionTeam}
                maxLength={400}
                placeholder="Veuillez saisir le nom de l'équipe"
                onChange={(e) => setConstructionTeam(e.target.value)}
              />
            </FormInput>

            <FormInput label={'Certifications'}>
              <InputText
                id="certifications"
                value={certifications}
                maxLength={400}
                placeholder="Veuillez saisir les certifications"
                onChange={(e) => setCertifications(e.target.value)}
              />
            </FormInput>
          </div>
        </div>
        {footerButtons}
      </div>
    </Sidebar>
  );
}

export default ProjectPanel;
